import { Component, ElementRef, OnInit, inject, signal } from '@angular/core'
import { SIDEBAR_MIN_WIDTH, SidebarService } from '@wm-front/core'
declare const $localize: any

/** Keyboard step (px) of the arrow keys */
const KEYBOARD_STEP = 16

/**
 * The left edge of the sidebar: drag it (or use the arrow keys) to resize the sidebar, double-click it to go back to
 * the default width (PRENG-8270). Place it inside the `mat-sidenav`.
 */
@Component({
  selector: 'wm-front-sidebar-resize-handle',
  template: '',
  styleUrl: './sidebar-resize-handle.component.scss',
  host: {
    role: 'separator',
    tabindex: '0',
    'aria-orientation': 'vertical',
    '[attr.aria-label]': 'label',
    '[attr.aria-valuenow]': '_sidebarService.width()',
    '[attr.aria-valuemin]': 'minWidth',
    '[class.dragging]': '_dragging()',
    '(pointerdown)': 'startDrag($event)',
    '(pointermove)': 'drag($event)',
    '(pointerup)': 'stopDrag()',
    '(pointercancel)': 'stopDrag()',
    '(dblclick)': '_sidebarService.resetWidth()',
    '(keydown.arrowleft)': 'nudge(1, $event)',
    '(keydown.arrowright)': 'nudge(-1, $event)',
    '(window:resize)': 'updateContentArea()',
  },
})
export class SidebarResizeHandleComponent implements OnInit {
  public label = $localize`:Accessible label | Handle on the left edge of the sidebar to resize it@@sidebarResizeHandleLabel:Redimensionner le panneau`

  public minWidth = SIDEBAR_MIN_WIDTH

  protected _sidebarService = inject(SidebarService)

  protected _dragging = signal(false)

  private _el = inject<ElementRef<HTMLElement>>(ElementRef)

  public ngOnInit(): void {
    this.updateContentArea()
  }

  /**
   * Starts resizing: the pointer is captured so the drag goes on outside the handle
   * @param { PointerEvent } event the pointer down
   */
  public startDrag(event: PointerEvent): void {
    event.preventDefault()
    this.updateContentArea()
    this._el.nativeElement.setPointerCapture(event.pointerId)
    this._dragging.set(true)
  }

  /**
   * Resizes while dragging: the sidebar is anchored to the right edge of the window
   * @param { PointerEvent } event the pointer move
   */
  public drag(event: PointerEvent): void {
    if (this._dragging()) {
      this._sidebarService.resize(window.innerWidth - event.clientX)
    }
  }

  /** Stops resizing */
  public stopDrag(): void {
    this._dragging.set(false)
  }

  /**
   * Widens (1) or narrows (-1) the sidebar by one keyboard step
   * @param { 1 | -1 } direction where to go
   * @param { Event } event the arrow key
   */
  public nudge(direction: 1 | -1, event: Event): void {
    event.preventDefault()
    this._sidebarService.resize(
      this._sidebarService.width() + direction * KEYBOARD_STEP
    )
  }

  /** The content area is the window minus the navigation menu (hidden on small screens) */
  public updateContentArea(): void {
    const menu = document.querySelector('wm-front-sidebar-menu')
    const menuWidth = menu?.getBoundingClientRect().width ?? 0

    this._sidebarService.setContentAreaWidth(window.innerWidth - menuWidth)
  }
}
